import { yupResolver } from '@hookform/resolvers/yup';
import { enqueueSnackbar } from 'notistack';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import * as Yup from 'yup';

import {
  Alert,
  Autocomplete,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  TextField,
  Typography,
} from '@mui/material';

import linkAnimation from 'src/assets/lottie/link.json';
import { useGetOfferByIdQuery, useNotifyOfferCandidateMutation } from 'src/services/applications/applications.service';
import { useGetEmailConnectionStateQuery, useGetEmailMessageTemplatesQuery, useLazyGetTemplatedContentQuery } from 'src/services/email-sync/email-sync.service';
import { EmailConnectionStatus, EmailMessageTemplate, EmailMessageTemplateType } from 'src/services/email-sync/email-sync.types';

import { debounce } from 'lodash';
import AnimationContainer from 'src/components/animation/container';
import FormProvider, { RHFEditor, RHFTextField } from 'src/components/hook-form';
import Iconify from 'src/components/iconify';
import { useRouter } from 'src/routes/hooks';
import { paths } from 'src/routes/paths';
import { closeNotifyCandidateOfferModal } from 'src/store/slices/applications/applicationsSlice';
import { useAppDispatch, useAppSelector } from 'src/store/store';

// ----------------------------------------------------------------------

type Props = {
  open: boolean;
};

export default function NotifyOfferCandidateModal({ open }: Props) {

  const { t } = useTranslation();
  const router = useRouter();
  const dispatch = useAppDispatch();

  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [templateSearchQuery, setTemplateSearchQuery] = useState<string | null>(null);
  const [selectedTemplate, setSelectedTemplate] = useState<EmailMessageTemplate | null>(null);

  const selectedOfferId = useAppSelector((state) => state.applications.selectedOfferId);
  const applicationsState = useAppSelector((state) => state.applications);

  const { currentData: connectionState, isLoading: isLoadingConnection } = useGetEmailConnectionStateQuery();
  const { data: offerData, isLoading: isLoadingOffer } = useGetOfferByIdQuery({ offerId: selectedOfferId as string }, { skip: !selectedOfferId });

  const [notifyCandidate, { isLoading: isNotifying }] = useNotifyOfferCandidateMutation();

  const {
    data: templatesData
  } = useGetEmailMessageTemplatesQuery({
    page: 1,
    per_page: 10,
    type: EmailMessageTemplateType.OFFER_CONTENT,
    search_query: templateSearchQuery || undefined,
  });

  const [getTemplatedContent, { isLoading: isGettingTemplatedContent }] = useLazyGetTemplatedContentQuery();

  // Form schema
  const NotifyFormSchema = Yup.object().shape({
    subject: Yup.string().required(t('email_sync.mailer.validation.subject_required')),
    content: Yup.string().required(t('email_sync.mailer.validation.body_required')),
  });

  // Form methods
  const methods = useForm({
    resolver: yupResolver(NotifyFormSchema),
    defaultValues: {
      subject: '',
      content: '',
    },
  });

  const { handleSubmit, reset } = methods;

  useEffect(() => {
    if (open) {
      reset({
        subject: '',
        content: '',
      });
      setErrorMessage(null);
    }
  }, [open, reset]);

  const handleClose = useCallback(() => {
    dispatch(closeNotifyCandidateOfferModal());
  }, [dispatch]);

  const onTemplateSearchQueryChange = useMemo(
    () => debounce((value: string) => {
      setTemplateSearchQuery(value);
    }, 500),
    [setTemplateSearchQuery]
  );

  const onSubmit = async (data: { subject: string; content: string }) => {
    try {
      setErrorMessage(null);

      await notifyCandidate({
        offerId: selectedOfferId as string,
        subject: data.subject,
        content: data.content,
      }).unwrap();

      enqueueSnackbar(t('email_sync.api.send_email.success'), {
        variant: 'success',
      });

      reset();
      handleClose();
    } catch (err) {
      setErrorMessage(t('email_sync.api.send_email.default_error'));
    }
  };

  const handleTemplateSelect = useCallback(async (template: EmailMessageTemplate | null) => {
    if (!template) {
      setSelectedTemplate(null);
      methods.reset({
        subject: '',
        content: '',
      });
      return;
    }

    if (offerData?.id) {
      try {
        const { data } = await getTemplatedContent({
          templateId: template.id,
          entityId: selectedOfferId as string,
        });

        setSelectedTemplate(template);
        methods.reset({
          subject: data?.subject || '',
          content: data?.content || '',
        });
      }
      catch (error) {
        console.error('Error fetching template content:', error);
      }
    }

  }, [methods, getTemplatedContent, offerData, selectedOfferId]);

  const viewConnectPage = useCallback(() => {
    handleClose();
    router.push(paths.dashboard.account.email);
  }, [router, handleClose]);

  const renderContent = () => {
    if (isLoadingConnection || isLoadingOffer) {
      return (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}>
          <CircularProgress />
        </Box>
      );
    }

    if (connectionState?.status !== EmailConnectionStatus.ACTIVE) {
      return (
        <Stack spacing={2} sx={{ p: 2 }} direction="column" alignItems="center">
          <AnimationContainer height={120} width={120} json={linkAnimation} loop />

          <Stack spacing={2} sx={{ p: 2 }} direction="column" alignItems="center" justifyContent="center">
            <Typography variant="body2" textAlign="center" sx={{ mb: 1 }}>
              {t('email_sync.mailer.connection_inactive')}
            </Typography>
            <Button
              variant="contained"
              endIcon={<Iconify icon="material-symbols:chat-paste-go-rounded" />}
              onClick={viewConnectPage}
            >
              {t('common.connect')}
            </Button>
          </Stack>
        </Stack>
      );
    }

    return (
      <FormProvider methods={methods} onSubmit={handleSubmit(onSubmit)}>
        <Stack spacing={3} sx={{ pb: 2 }}>
          {errorMessage && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {errorMessage}
            </Alert>
          )}

          <Alert severity="info" sx={{ mb: 2 }}>
            {t('applications.notify_candidate_modal.info_alert')}
          </Alert>

          {/* Candidate Email (Read-only) */}
          <TextField
            fullWidth
            label={t('applications.notify_candidate_modal.form.candidate')}
            value={offerData?.candidate_name || ''}
            disabled
          />

          <Box>
            <Autocomplete
              multiple={false}
              options={templatesData?.results as EmailMessageTemplate[] ?? []}
              value={selectedTemplate as EmailMessageTemplate}
              onInputChange={(_e, value) => onTemplateSearchQueryChange(value)}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label={t(
                    'email_sync.mailer.template_search_label'
                  )}
                  placeholder={t(
                    'email_sync.mailer.template_search_placeholder'
                  )}
                />
              )}
              onChange={(_e, value: EmailMessageTemplate | null) => handleTemplateSelect(value)}
              renderOption={({ key, ...rest }, option: EmailMessageTemplate) => (
                <li key={key} {...rest}>
                  <Stack direction="column">
                    <Typography variant="body1">
                      {`${option.name}`}
                    </Typography>
                  </Stack>
                </li>
              )}
              getOptionLabel={(option) => `${option.name}` as string}
              isOptionEqualToValue={(option, value) =>
                option && (option as EmailMessageTemplate).id === (value as EmailMessageTemplate).id
              }
              getOptionKey={(option) => (option as EmailMessageTemplate).id as string}
            />
          </Box>

          {/* Subject */}
          <RHFTextField name="subject" label={t('email_sync.mailer.subject')} />

          {/* Content */}
          <RHFEditor
            name="content"
            label={t('email_sync.mailer.message')}
            placeholder={t('email_sync.mailer.message_placeholder')}
            simple
          />
        </Stack>
      </FormProvider>
    );
  };

  return (
    <Dialog open={open} onClose={handleClose} fullWidth maxWidth="sm">
      <DialogTitle>{t('applications.notify_candidate_modal.title')}</DialogTitle>

      <DialogContent>
        {renderContent()}
      </DialogContent>

      {connectionState?.status === EmailConnectionStatus.ACTIVE && (
        <DialogActions>
          <Button onClick={handleClose}>{t('common.cancel')}</Button>
          <Button
            variant="contained"
            onClick={handleSubmit(onSubmit)}
            disabled={isNotifying}
            startIcon={isNotifying ? <CircularProgress size={20} /> : null}
          >
            {t('email_sync.mailer.send_button')}
          </Button>
        </DialogActions>
      )}
    </Dialog>
  );
}